import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable, usePermanentLocations, usePermanentRealtimeDealers } from "components";
import { useRegisterNavAction } from "hooks";
import { DeviceUserHistoryModal } from "modules/DevicesDashboard/components";
import { setDevicesFilters, useDevicesFilters } from "modules/DevicesDashboard/components/DevicesDashboardFilters/hooks";
import "modules/DevicesDashboard/components/DevicesDashboardTable/DevicesDashboardTable.scss";
import { useDevicesQuery } from "modules/DevicesDashboard/hooks";
import { SORT_BY } from "modules/DevicesDashboard/queryKeys";
import { formatDate } from "utils/dateHelperFunctions";

import type { AccessorColumnDef, OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { Device } from "models";
import type { PAGE_SIZE } from "utils/common";

type SortHeaderProps = {
  label: string;
  ascending: SORT_BY;
  descending: SORT_BY;
  order_by: SORT_BY | null;
  onSort: (ascending: SORT_BY, descending: SORT_BY) => void;
};

const SortHeader = ({ label, ascending, descending, order_by, onSort }: SortHeaderProps) => {
  const isActive = order_by === ascending || order_by === descending;

  return (
    <div className="sortable-header" onClick={() => onSort(ascending, descending)}>
      <span className="no-wrap-text">{label}</span>
      <Icon className={`caret ${order_by === ascending ? "up" : "down"} ${isActive ? "" : "inactive"}`} />
    </div>
  );
};

export const DevicesDashboardTable = () => {
  const { t } = useTranslation();

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  const { locations } = usePermanentLocations();
  const { dealers } = usePermanentRealtimeDealers();
  const { page: currentPage, page_size: pageSize, order_by } = useDevicesFilters();
  const { data: devices, refetch: refetchDevices, isLoading: isDevicesLoading } = useDevicesQuery();
  useRegisterNavAction(() => refetchDevices());

  const nbPages = devices?.nb_pages || 0;

  const handleSortChange = useCallback(
    (ascending: SORT_BY, descending: SORT_BY) => setDevicesFilters({ order_by: order_by === descending ? ascending : order_by === ascending ? null : descending }),
    [order_by]
  );

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater({ pageIndex: currentPage - 1, pageSize }) : updater;
    setDevicesFilters({ page: newState.pageIndex + 1 });
  };
  const handlePageSizeChange = (page_size: PAGE_SIZE) => setDevicesFilters({ page_size, page: 1 });

  const columns = useMemo<AccessorColumnDef<Device>[]>(
    () => [
      {
        id: "last_used",
        accessorFn: row => row,
        header: () => (
          <SortHeader
            order_by={order_by}
            label={t("v8_last_used", "Last Used")}
            ascending={SORT_BY.LastUsedAscending}
            descending={SORT_BY.LastUsedDescending}
            onSort={handleSortChange}
          />
        ),
        cell: ({ row: { original } }) =>
          original.last_used && (
            <span className="no-wrap-text">
              {`${formatDate(original.last_used, DATE_FORMATS_FNS.dateMonthYear)} | ${formatDate(original.last_used, DATE_FORMATS_FNS.hoursMinutesSeconds)}`}
            </span>
          )
      },
      {
        id: "current_user",
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_current_user", "Current User")}</span>,
        cell: ({ row: { original } }) => (original.current_user ? `${original.current_user.first_name} ${original.current_user.last_name}` : "")
      },
      {
        id: "dealer",
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_dealer", "Dealer")}</span>,
        cell: ({ row: { original } }) => dealers.find(dealer => dealer.id === original.current_user?.dealer_id)?.name ?? ""
      },
      {
        id: "location",
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_location", "Location")}</span>,
        cell: ({ row: { original } }) => locations.find(location => location.id === original.current_user?.dealer_location_id)?.name ?? ""
      },
      {
        id: "brand",
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_brand", "Brand")}</span>,
        cell: ({ row: { original } }) => original.brand
      },
      {
        id: "model",
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_model", "Model")}</span>,
        cell: ({ row: { original } }) => original.model
      },
      {
        id: "os_version",
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_os_version", "OS Version")}</span>,
        cell: ({ row: { original } }) => original.os_version
      },
      {
        id: "free_space",
        accessorFn: row => row,
        header: () => (
          <SortHeader
            order_by={order_by}
            label={t("v8_free_space", "Free Space")}
            ascending={SORT_BY.FreeSpaceAscending}
            descending={SORT_BY.FreeSpaceDescending}
            onSort={handleSortChange}
          />
        ),
        cell: ({ row: { original } }) => original.free_space
      },
      {
        id: "mac_address",
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_mac_address", "MAC Address")}</span>,
        cell: ({ row: { original } }) => original.mac_address
      },
      {
        id: "email_address",
        accessorFn: row => row,
        header: () => <span className="no-wrap-text">{t("v8_email_address", "Email Address")}</span>,
        cell: ({ row: { original } }) => original.current_user?.email
      },
      {
        id: "actions",
        accessorFn: row => row,
        header: () => null,
        cell: ({ row: { original } }) => {
          if (!original.user_history?.length) return null;

          return (
            <div className="user-card-container" onClick={() => setSelectedDevice(original)}>
              <div className="user-card-user">
                <Icon className="user user-card-icon" />
              </div>
            </div>
          );
        }
      }
    ],
    [t, dealers, locations, order_by, handleSortChange]
  );

  return (
    <>
      <div className="DevicesDashboardTable">
        <ReactTable
          isFetching={isDevicesLoading}
          columns={columns}
          data={devices?.items || []}
          pagination={nbPages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined}
          pageCount={nbPages}
          pageSize={pageSize}
          totalItems={devices?.nb_items || 0}
          onPaginationChange={handlePaginationChange}
          onChangePageSize={handlePageSizeChange}
        />
      </div>
      <DeviceUserHistoryModal device={selectedDevice} onClose={() => setSelectedDevice(null)} />
    </>
  );
};
